import React, { useEffect, useMemo, useState } from "react";
import { BsArrowLeft } from "react-icons/bs";
import { RiArrowDropDownFill } from "react-icons/ri";
import { FiSearch } from "react-icons/fi";

import useAuthAxios from "../../../utils/baseAxios";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { API } from "../../../constants/api";
import { errorMessage } from "../../../utils/errorMessage";
import ClipLoader from "react-spinners/ClipLoader";
import {
  formatReceiptLabel,
  getRatesForReceiptKey,
  NO_RECEIPT_RATE_KEYS,
  PHYSICAL_RECEIPT_TYPES,
} from "../../../utils/giftcardReceiptType";

const SelectCard = ({
  setOpenSelectCard,
  setOpenSelectCardValue,
  setCardId,
  cardId,
  cardName,
  setCardName,
  cardImg,
  setCardImg,
  setCard,
  cardType,
  setCardType,
  receiptName,
  setReceiptName,
}: any) => {
  const [countryName, setCountryName] = useState<string>("");
  const [receipt, setReceipt] = useState<boolean>(false);
  const [receiptDropdownOpen, setReceiptDropdownOpen] =
    useState<boolean>(false);
  const [cashReceipt, setCashReceipt] = useState<any[]>([]);
  const [actReceipt, setActReceipt] = useState<any[]>([]);
  const [creditReceipt, setCreditReceipt] = useState<any[]>([]);
  const [debitReceipt, setDebitReceipt] = useState<any[]>([]);
  const [noReceipt, setNoReceipt] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // const queryClient = useQueryClient();

  const [countryDD, setCountryDD] = useState<boolean>(false);
  const axiosInstance = useAuthAxios();
  const getGiftcards = async () => {
    const response = await axiosInstance.get(API.getGiftCards);
    return response.data.data;
  };

  const {
    data: giftcards,
    error: error1,
    isLoading: loading1,
  } = useQuery({
    queryKey: ["get-giftcards"],
    queryFn: getGiftcards,
    retry: 1,
  });

  const isRateActive = (rate: any) => rate?.isActive !== false;

  const getDefaultCountryName = (card: any, type: string) => {
    if (!card?.rates) return "";
    const matchingRate = card.rates.find(
      (rate: any) =>
        isRateActive(rate) &&
        (type === "ecode" ? rate.type === "ecode" : rate.type !== "ecode")
    );
    return matchingRate?.country || "";
  };

  const setDefaultPhysicalRate = (cardRates: any) => {
    const priorityOrder = [...PHYSICAL_RECEIPT_TYPES];
    for (const key of priorityOrder) {
      const bucket = getRatesForReceiptKey(cardRates, key);
      if (bucket.length > 0) {
        const isNoReceipt = NO_RECEIPT_RATE_KEYS.includes(key);
        setReceipt(!isNoReceipt);
        setReceiptDropdownOpen(false);
        setReceiptName(isNoReceipt ? "physicalNoReceipt" : key);
        setCard(bucket);
        return;
      }
    }
    setCard([]);
    setReceipt(false);
    setReceiptDropdownOpen(false);
    setReceiptName("");
  };

  const getCardDetails = async () => {
    const response = await axiosInstance.get(API.getGiftCardDetails(cardId));
    const giftCard = response.data.data.giftCard;
    const rates = giftCard?.rates || [];

    // Filter rates for physical cards - any rate that is NOT ecode is considered physical
    // (e.g., physicalCashReceipt, physicalActivationReceipt, physicalNoReceipt, etc.)
    const filteredRates = rates.filter(
      (rate: any) => isRateActive(rate) && rate.type !== "ecode"
    );

    // Group by country
    const groupedData = filteredRates.reduce((result: any, item: any) => {
      const country = item.country?.toLowerCase() || "";

      if (!country) return result;

      if (!result[country]) {
        result[country] = {
          country: item.country,
          rates: {},
        };
      }

      const type = item.type;
      if (!result[country].rates[type]) {
        result[country].rates[type] = [];
      }

      result[country].rates[type].push({
        range: item.range,
        rate: item.rate,
        _id: item._id,
      });

      return result;
    }, {});

    return Object.values(groupedData);
  };
  const {
    data: cardDetails,
    error: error2,
    isLoading: loading2,
    refetch: refetch1,
  } = useQuery({
    queryKey: ["get-card-details", cardId, cardType],
    queryFn: getCardDetails,
    enabled: !!cardId && cardType !== "ecode",
    retry: 1,
  });

  const getECardDetails = async () => {
    const response = await axiosInstance.get(API.getGiftCardDetails(cardId));
    const giftCard = response.data.data.giftCard;
    const rates = giftCard?.rates || [];

    // Filter rates by type (ecode only)
    const filteredRates = rates.filter(
      (rate: any) => isRateActive(rate) && rate.type === "ecode"
    );

    // Group by country
    const groupedData = filteredRates.reduce((result: any, item: any) => {
      const country = item.country?.toLowerCase() || "";

      if (!country) return result;

      if (!result[country]) {
        result[country] = {
          country: item.country,
          rates: { ecode: [] },
        };
      }

      result[country].rates.ecode.push({
        range: item.range,
        rate: item.rate,
        _id: item._id,
      });

      return result;
    }, {});

    return Object.values(groupedData);
  };
  const {
    data: ecardDetails,
    error: error3,
    isLoading: loading3,
    refetch: refetchEcode,
  } = useQuery({
    queryKey: ["get-ecard-details", cardId, cardType],
    queryFn: getECardDetails,
    enabled: !!cardId && cardType === "ecode",
    retry: 1,
  });
  useEffect(() => {
    if (error1) {
      const newError = error1 as any;
      toast.error(errorMessage(newError?.message || newError?.data?.message));
    }
    if (error2) {
      const newError = error2 as any;
      toast.error(errorMessage(newError?.message || newError?.data?.message));
    }
    if (error3) {
      const newError = error3 as any;
      toast.error(errorMessage(newError?.message || newError?.data?.message));
    }
  }, [error1, error2, error3]);
  useEffect(() => {
    if (cardId && cardType !== "ecode") {
      refetch1();
    } else if (cardId && cardType === "ecode") {
      refetchEcode();
    }
  }, [cardId, cardType, refetch1, refetchEcode]);

  // Show every card the API returns (like the mobile app) — the physical/ecode
  // filter is applied at the country step, not on the card grid.
  const filteredGiftcards = useMemo(() => {
    const list = giftcards?.giftCards || [];
    if (!searchTerm.trim()) return list;
    return list.filter((card: any) =>
      card.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [giftcards, searchTerm]);

  return (
    <div className="fixed inset-0  flex font-manrope justify-start overflow-auto pb-16 items-start pt-16 bg-[#E7EBEE] dark:bg-primary_dark dark:bg-opacity-95  backdrop-blur-sm">
      <div
        className={` w-11/12 mds:w-8/12 md:7/12 border dark:border-[#303030] bg-white  rounded-xl mx-auto p-6 dark:bg-[#192025]   lgss:w-2/5 xxl:w-1/3 `}
      >
        <div className="w-full flex justify-start gap-6 items-center">
          <button
            onClick={() => {
              setOpenSelectCard(false);
            }}
          >
            <BsArrowLeft className="text-black dark:text-white text-[20px]" />
          </button>
          <h4 className="text-gray-900 dark:text-gray-100 font-sora font-semibold text-[20px]">
            Sell Giftcard
          </h4>
        </div>
        <div className="w-full mt-8">
          <div className="w-full">
            <div className="relative">
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-[48px] rounded-xl border border-gray-200 dark:border-gray-700 bg-transparent pl-4 text-[14px] text-gray-700 dark:text-gray-200 outline-none"
              />
              <FiSearch className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-[18px]" />
            </div>
            <h4 className="text-[14px] text-gray-800 dark:text-gray-200 mt-6">
              Card type
            </h4>
            <div className="grid grid-cols-2 gap-4 mt-4 w-full">
              {["physical", "ecode"].map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => {
                    setCardType(type);
                    setCountryDD(false);
                    setReceipt(false);
                    setReceiptDropdownOpen(false);
                    setReceiptName("");
                    // Keep the selected card (like the mobile app); just
                    // recompute the default country for the new type.
                    const selectedCard = cardId
                      ? giftcards?.giftCards?.find(
                          (c: any) => c._id === cardId
                        )
                      : null;
                    setCountryName(
                      selectedCard
                        ? getDefaultCountryName(selectedCard, type) || ""
                        : ""
                    );
                  }}
                  className={
                    cardType === type
                      ? "border-2 border-secondary_button  h-[72px] flex justify-start px-4 items-center gap-3 rounded-xl text-[14px] font-semibold bg-transparent text-secondary_button shadow-sm"
                      : "border border-gray-200 dark:border-gray-600 h-[72px] flex justify-start px-4 items-center gap-3 rounded-xl text-[14px] text-gray-800 dark:text-gray-200 bg-transparent dark:bg-transparent"
                  }
                >
                  <span
                    className={`w-[22px] h-[22px] rounded-full border flex items-center justify-center ${
                      cardType === type
                        ? "border-secondary_button"
                        : "border-gray-400"
                    }`}
                  >
                    <span
                      className={`w-[12px] h-[12px] rounded-full ${
                        cardType === type ? "bg-secondary_button" : ""
                      }`}
                    />
                  </span>
                  {type === "physical" ? "Physical cards" : "E-code"}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6 w-full">
            <div className="flex items-center justify-between">
              <h4 className="text-[14px] text-gray-800 dark:text-gray-200">
                Select Giftcard
              </h4>
              {cardName && (
                <span className="text-secondary_button text-[12px] font-medium">
                  Selected: {cardName}
                </span>
              )}
            </div>
            <div className="mt-4 min-h-[120px]">
              {loading1 ? (
                <div className="w-full pt-8 flex justify-center items-center">
                  <ClipLoader color="#0B84CB" size={40} />
                </div>
              ) : filteredGiftcards.length ? (
                <div className="grid grid-cols-3 gap-4">
                  {filteredGiftcards.map((activeCard: any) => (
                    <button
                      key={activeCard._id}
                      onClick={() => {
                        setCardId(activeCard._id);
                        setCardImg(activeCard.thumbnail);
                        setCardName(activeCard.name);
                        const defaultCountry = getDefaultCountryName(
                          activeCard,
                          cardType
                        );
                        setCountryName(defaultCountry || "");
                        setReceiptName("");
                        setReceipt(false);
                        setReceiptDropdownOpen(false);
                      }}
                      className={`flex flex-col items-center gap-3 rounded-xl border py-4 transition-all ${
                        cardId === activeCard._id
                          ? "border-2 border-secondary_button bg-[#F0F7FF]"
                          : "border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent"
                      }`}
                    >
                      <div className="h-[36px] w-[36px]">
                        <img
                          src={activeCard.thumbnail}
                          className="h-full w-full object-cover rounded-full"
                          alt={activeCard.name}
                        />
                      </div>
                      <span className="text-[13px] font-semibold text-gray-800 dark:text-gray-100">
                        {activeCard.name}
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 dark:text-gray-300 text-[13px] py-8">
                  No giftcard found for "{searchTerm}"
                </div>
              )}
            </div>
          </div>

          {cardId && (
            <div className="mt-6 w-full relative">
              <h4 className="text-[14px] text-gray-800 dark:text-gray-200">
                Select Country
              </h4>
              <div
                onClick={() => {
                  setCountryDD((prev) => !prev);
                  setReceiptDropdownOpen(false);
                }}
                className="w-full cursor-pointer border-gray-300 dark:border-gray-600 h-[48px]  rounded-xl mt-2 border px-2 flex justify-between items-center"
              >
                <div className="flex justify-start items-center gap-3">
                  <h4 className="text-[14px] text-gray-700 dark:text-gray-300">
                    {countryName ? countryName : "Select country"}
                  </h4>
                </div>
                <RiArrowDropDownFill className="text-[28px] text-gray-700 dark:text-gray-300" />
              </div>
              {countryDD && cardType !== "ecode" ? (
                <div className="absolute z-50 rounded-xl w-full mt-2 p-4 h-[250px] overflow-auto bg-white dark:bg-gray-800 ">
                  {loading2 ? (
                    <div className="w-full pt-8 flex justify-center items-center">
                      <ClipLoader color="#0B84CB" size={40} />
                    </div>
                  ) : (
                    <div className="w-full">
                      {cardDetails && cardDetails?.length >= 1 ? (
                        <div className="w-full">
                          {cardDetails?.map((card: any, index: any) => (
                            <button
                              key={index}
                              onClick={() => {
                                setCountryName(card.country);
                                setCountryDD(false);
                                setCashReceipt(
                                  card.rates?.physicalCashReceipt || []
                                );
                                setDebitReceipt(
                                  card.rates?.physicalDebitCardReceipt || []
                                );
                                setActReceipt(
                                  card.rates?.physicalActivationReceipt || []
                                );
                                setCreditReceipt(
                                  card.rates?.physicalCreditCardReceipt || []
                                );
                                setNoReceipt(
                                  getRatesForReceiptKey(
                                    card.rates,
                                    "physicalNoReceipt"
                                  )
                                );
                                setDefaultPhysicalRate(card.rates);
                              }}
                              className="py-4 w-full border-b border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 flex justify-start items-center gap-3"
                            >
                              {card.country}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <h4 className="text-[14px] lgss:text-[16px] font-bold font-sora text-[#042741] dark:text-white">
                          No card available for trading at the moment.
                        </h4>
                      )}
                    </div>
                  )}
                </div>
              ) : null}
              {countryDD && cardType === "ecode" ? (
                <div className="absolute z-50 rounded-xl w-full mt-2 p-4 h-[250px] border border-gray-200 dark:border-gray-600 overflow-auto bg-white dark:bg-gray-800 ">
                  {loading3 ? (
                    <div className="w-full pt-8 flex justify-center items-center">
                      <ClipLoader color="#0B84CB" size={40} />
                    </div>
                  ) : (
                    <div className="w-full">
                      {ecardDetails && ecardDetails?.length >= 1 ? (
                        <div className="w-full">
                          {ecardDetails?.map((card: any, index: any) => (
                            <button
                              key={index}
                              onClick={() => {
                                setCountryName(card.country);
                                setCountryDD(false);
                                setCard(card.rates.ecode);
                                setReceiptName("");
                                setReceipt(false);
                                setReceiptDropdownOpen(false);
                              }}
                              className="py-4 w-full border-b border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 flex justify-start items-center gap-3"
                            >
                              {card.country}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <h4 className="text-[14px] font-medium font-sora text-[#042741] dark:text-white">
                          No card available for trading at the moment.
                        </h4>
                      )}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}
          {receipt && (
            <button
              onClick={() => {
                if (cardId) {
                  setReceiptDropdownOpen((prev) => !prev);
                }
              }}
              className={`w-full cursor-pointer border-gray-300 dark:border-gray-600 h-[48px]  rounded-xl mt-4 border px-2 flex text-[15px] justify-between items-center text-gray-700 dark:text-gray-300 `}
            >
              {receiptName
                ? formatReceiptLabel(receiptName)
                : "Select receipt type"}
              <RiArrowDropDownFill className="text-[28px] dark:text-[#E8F3FC]" />
            </button>
          )}
          {receipt && receiptDropdownOpen ? (
            <div
              className={
                "w-full absolute transform transition-transform dark:bg-[#384e5f] duration-300  top-full left-0 mt-2 px-4 shadow-xl rounded-xl  overflow-y-auto bg-[#D4EBFD] scrollbar-thumb-[#60B7F6] p-4 dark:scrollbar-thumb-[#AEBCC6]  "
              }
            >
              {cardType !== "ecode" && cashReceipt?.length >= 1 && (
                <div
                  onClick={() => {
                    setReceiptName("physicalCashReceipt");
                    setReceiptDropdownOpen(false);
                    setCard(cashReceipt);
                  }}
                  className="cursor-pointer"
                >
                  <h4 className="text-[18px] font-bold font-sora  mt-2 text-[#042741] dark:text-white">
                    Cash receipt
                  </h4>
                  <div className="mt-3 w-full h-[2px] bg-gray-300 dark:bg-gray-500" />
                </div>
              )}
              {cardType !== "ecode" && creditReceipt?.length >= 1 && (
                <div
                  onClick={() => {
                    setReceiptName("physicalCreditCardReceipt");
                    setReceiptDropdownOpen(false);
                    setCard(creditReceipt);
                  }}
                  className="cursor-pointer"
                >
                  <h4 className="text-[18px] font-bold mt-2 font-sora text-[#042741] dark:text-white">
                    Credit card receipt
                  </h4>
                  <div className="mt-3 w-full h-[2px] bg-gray-300 dark:bg-gray-500" />
                </div>
              )}
              {cardType !== "ecode" && debitReceipt?.length >= 1 && (
                <div
                  onClick={() => {
                    setReceiptName("physicalDebitCardReceipt");
                    setReceiptDropdownOpen(false);
                    setCard(debitReceipt);
                  }}
                  className="cursor-pointer"
                >
                  <h4 className="text-[18px] font-bold font-sora mt-2 text-[#042741] dark:text-white">
                    Debit card receipt
                  </h4>
                  <div className="mt-3 w-full h-[2px] bg-gray-300 dark:bg-gray-500" />
                </div>
              )}
              {cardType !== "ecode" && actReceipt?.length >= 1 && (
                <div
                  onClick={() => {
                    setReceiptName("physicalActivationReceipt");
                    setReceiptDropdownOpen(false);
                    setCard(actReceipt);
                  }}
                  className="cursor-pointer"
                >
                  <h4 className="text-[18px] font-bold font-sora mt-2 text-[#042741] dark:text-white">
                    Activation receipt
                  </h4>
                  <div className="mt-3 w-full h-[2px] bg-gray-300 dark:bg-gray-500" />
                </div>
              )}
              {cardType !== "ecode" && noReceipt?.length >= 1 && (
                <div
                  onClick={() => {
                    setReceiptName("physicalNoReceipt");
                    setReceiptDropdownOpen(false);
                    setCard(noReceipt);
                  }}
                  className="cursor-pointer"
                >
                  <h4 className="text-[18px] font-bold font-sora mt-2 text-[#042741] dark:text-white">
                    No receipt
                  </h4>
                  <div className="mt-3 w-full h-[2px] bg-gray-300 dark:bg-gray-500" />
                </div>
              )}
            </div>
          ) : null}
          <button
            disabled={!cardName || !countryName}
            onClick={() => {
              setOpenSelectCard(false);
              setOpenSelectCardValue(true);
            }}
            className={
              !cardName || !countryName
                ? "mt-8 bg-gray-500 flex justify-center items-center text-gray-100 text-[15px] font-medium h-[48px] w-full rounded-xl"
                : "mt-8 bg-secondary_button flex justify-center items-center text-white text-[15px] font-medium h-[48px] w-full rounded-xl"
            }
          >
            Proceed
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectCard;
